import {
  Button,
  Fab,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocation, useNavigate } from 'react-router';
import { ONLINE_MEDIA_CLIPPING } from 'src/route';
import { useQuery } from '@apollo/client';
import { ONLINE_MEDIA_GET_CLIPPING_BY_ID_V5 } from 'src/services/graphql/onlineMedia/query';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import useDownload from 'src/services/useDownload';
import useOnlineMedia from 'src/services/useOnlineMedia';
import { useAlert } from 'src/hooks/useAlert';
import useQueryUrl from 'src/hooks/useQuery';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { generateWhatsappReportDoc } from 'src/utils/generateWhatsappReportDoc';
import { generateArticleBySentimentDoc } from 'src/utils/generateArticleBySentimentDoc';
import { Packer } from 'docx';
import saveAs from 'file-saver';
import { downloadCsv } from 'src/utils/downloadCsv';
import { downloadXlsx } from 'src/utils/downloadXlsx';
import { TExportType } from 'src/models/download';

const TopHeaderClippingById = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const { pathname } = useLocation();
  const id = pathname.split('/')[3];

  const { data } = useQuery(ONLINE_MEDIA_GET_CLIPPING_BY_ID_V5, {
    variables: { clippingId: id }
  });

  const clippingName = data?.onlineMedia_getClippingByIdV5?.name ?? '';

  const { handleClickAlert } = useAlert();
  const query = useQueryUrl();
  const tabClipping = query.get('tab');
  const dispatch = useAppDispatch();

  const { downloadClusteringCsv } = useOnlineMedia();
  const { downloadCsvMediaOnline } = useDownload();

  const projectId = useAppSelector((state) => state.users.project.id);
  const advanceSearchOnlineMedia = useAppSelector(
    (state) => state.storeAdvanceSearchOnlineMedia
  );
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );
  const {
    articleList: articles,
    languageSelected,
    clusteringConfig,
    streamSelected
  } = useAppSelector((state) => state.storeOnlineMediaStream);

  const labels = useAppSelector((state) => state.storeLabel.labelArticle.data);
  const timezone = useAppSelector((state) => state.users.project.timezone);

  const fillAdvanceSearchOnlineMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchOnlineMedia);
  }, [advanceSearchOnlineMedia]);

  const payloads = {
    projectId,
    timezone,
    start: dateRangeFormatted.start,
    end: dateRangeFormatted.end,
    granularity: {
      unit: 'day',
      value: 1
    },
    ...fillAdvanceSearchOnlineMedia,
    clippingId: id,
    labelList: fillAdvanceSearchOnlineMedia.labelList.map(
      (label: any) => label.id || label
    ),
    languageList: languageSelected === 'all-language' ? [] : [languageSelected]
  };

  const [anchorElDownload, setAnchorElDownload] =
    useState<HTMLButtonElement | null>(null);

  const handleClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
  };

  const handleDownload = (exportType: TExportType = 'csv') => {
    handleCloseDownload();
    tabClipping !== 'cluster'
      ? downloadCsvMediaOnline({
          variables: { ...payloads, exportType }
        })
      : downloadClusteringCsv({
          variables: {
            ...payloads,
            ...clusteringConfig
          },
          onCompleted: () =>
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              severity: 'success',
              message:
                'It may take some time for the report to be processed.\n The report can be downloaded from the Activity List on the top right corner.'
            }),
          onError(error) {
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              severity: 'error',
              message: 'Failed to download report, please try again.'
            });
            console.log(error.message);
          }
        });
  };

  const handleDownloadWhatsappReport = () => {
    const doc = generateWhatsappReportDoc(articles.data.result);
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `ONM - ${clippingName}.docx`);
    });
    handleCloseDownload();
  };

  const handleDownloadBySentiment = () => {
    const [selectedLabelId] = advanceSearchOnlineMedia.labelList;
    const labelName = labels.find(
      (label: any) => label.id === selectedLabelId
    )?.displayName;
    const doc = generateArticleBySentimentDoc(
      articles.data.result,
      labelName ?? clippingName
    );
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `ONM - ${labelName ?? clippingName}.docx`);
    });
    handleCloseDownload();
  };

  const openDownload = Boolean(anchorElDownload);
  const idPopup = openDownload ? 'simple-popover' : undefined;

  return (
    <Stack
      direction={matchesUpMd ? "row" : 'column' }
      justifyContent="space-between"
      spacing={2}
      alignItems={matchesUpMd ? "center" : "flex-start"}
      sx={{ mb: 2, background: 'transparent' }}
    >
      <Stack direction="row" alignItems="center">
        <Fab
          sx={{
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[200]}`,
            mr: theme.spacing(2),
            boxShadow: 'none',
            '&:hover': { background: '#f5f5f5' }
          }}
          size="small"
          onClick={() => navigate(ONLINE_MEDIA_CLIPPING)}
        >
          <ArrowBackIcon />
        </Fab>
        <Typography variant="h1" fontWeight={700}>
          {clippingName}
        </Typography>
      </Stack>

      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          height: 40,
          borderRadius: '32px',
          bgcolor: 'white',
          color: theme.palette.primary.main,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          boxShadow: 'none',
          border: `1px solid ${theme.palette.primary.main}`,
          '&:hover': {
            bgcolor: 'rgba(0, 159, 227, 0.04)',
            borderColor: theme.palette.primary.dark,
            border: `1px solid ${theme.palette.primary.dark}`,
            boxShadow: 'none'
          },
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            color: theme.palette.primary.main
          }
        }}
        onClick={handleClickDownload}
      >
        Download Report
      </Button>

      <Popover
        id={idPopup}
        open={openDownload}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownload}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{ mt: 1 }}
      >
        <List sx={{ minWidth: 200 }}>
          <ListItemButton onClick={() => handleDownload()}>
            <ListItemText primary="CSV - All Metrics" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownload('xlsx')}>
            <ListItemText primary="XLSX - All Metrics" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadWhatsappReport()}>
            <ListItemText primary="Word - Whatsapp Report" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadBySentiment()}>
            <ListItemText primary="Word - Articles by Label" />
          </ListItemButton>
          {streamSelected && streamSelected.length > 0 && (
            <>
              <ListItemButton
                onClick={() =>
                  downloadCsv(
                    'custom',
                    streamSelected,
                    `selected-stream-${clippingName}`
                  )
                }
              >
                <ListItemText primary="CSV - Selected Stream" />
              </ListItemButton>
              <ListItemButton
                onClick={() =>
                  downloadXlsx(
                    'custom',
                    streamSelected,
                    `selected-stream-${clippingName}`,
                    'Articles'
                  )
                }
              >
                <ListItemText primary="XLSX - Selected Stream" />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>
    </Stack>
  );
};

export default TopHeaderClippingById;
